import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';

/**
 * Safely format backend enum-style values.
 *
 * Example:
 *
 * READY_FOR_REVIEW -> Ready For Review
 */
function formatEnumLabel(value) {
  if (!value) {
    return '';
  }

  return String(value)
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(' ');
}

/**
 * Clinical-note final document controls.
 *
 * Responsibilities:
 *
 * - display the current note workflow state
 * - request insurer-facing HTML preview from Spring Boot
 * - show the HTML preview safely inside a sandboxed iframe
 * - generate/open the backend PDF
 * - download the backend PDF
 *
 * The frontend does not recreate the clinical note layout.
 *
 * The Spring Boot renderer remains the single source of truth for:
 *
 * - note template selection
 * - payer-specific display rules
 * - section ordering
 * - signature display
 * - Plan of Care display
 * - insurer-facing formatting
 * - PDF validation
 */
export default function ClinicalNoteFinalDocumentPanel({
  clinicalNoteId,
  enabled = true,
  refreshToken = 0,
  onVersionChange,
}) {
  const [
    note,
    setNote,
  ] = useState(null);

  const [
    previewHtml,
    setPreviewHtml,
  ] = useState('');

  const [
    previewVisible,
    setPreviewVisible,
  ] = useState(false);

  const [
    loadingStatus,
    setLoadingStatus,
  ] = useState(false);

  const [
    loadingPreview,
    setLoadingPreview,
  ] = useState(false);

  const [
    openingPdf,
    setOpeningPdf,
  ] = useState(false);

  const [
    downloadingPdf,
    setDownloadingPdf,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    successMessage,
    setSuccessMessage,
  ] = useState('');

  /**
   * Publish a newly loaded optimistic-lock version to the parent.
   */
  const publishVersion =
    useCallback(
      (response) => {
        if (
          response?.entityVersion !==
            null &&
          response?.entityVersion !==
            undefined &&
          typeof onVersionChange ===
            'function'
        ) {
          onVersionChange(
            response.entityVersion,
            response
          );
        }
      },
      [onVersionChange]
    );

  /**
   * Refresh the note header/workflow information.
   *
   * This does not regenerate the preview automatically because an
   * HTML/PDF render can be more expensive than a normal note-header
   * request.
   */
  const loadStatus =
    useCallback(
      async () => {
        if (
          !enabled ||
          !clinicalNoteId
        ) {
          return;
        }

        setLoadingStatus(true);
        setError('');

        try {
          const response =
            await clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              );

          setNote(
            response
          );

          publishVersion(
            response
          );
        } catch (loadError) {
          setError(
            loadError?.message ||
              'Clinical-note document status could not be loaded.'
          );
        } finally {
          setLoadingStatus(false);
        }
      },
      [
        clinicalNoteId,
        enabled,
        publishVersion,
      ]
    );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    loadStatus();
  }, [
    loadStatus,
    refreshToken,
    enabled,
  ]);

  /**
   * A parent refresh means one or more clinical sections may have
   * changed.
   *
   * Remove an old rendered preview rather than leaving stale
   * insurer-facing information on screen.
   */
  useEffect(() => {
    if (refreshToken === 0) {
      return;
    }

    setPreviewHtml('');
    setPreviewVisible(false);
  }, [refreshToken]);

  /**
   * Generate the final HTML preview using the backend renderer.
   *
   * Spring Boot decides whether the current note state is valid for
   * rendering. The React application does not duplicate those rules.
   */
  async function loadPreview() {
    if (
      !clinicalNoteId ||
      loadingPreview
    ) {
      return;
    }

    setLoadingPreview(true);
    setError('');
    setSuccessMessage('');

    try {
      const html =
        await clinicalNoteService
          .getFinalPreviewHtml(
            clinicalNoteId
          );

      if (
        !html ||
        !String(html).trim()
      ) {
        throw new Error(
          'The clinical-note preview was empty.'
        );
      }

      setPreviewHtml(
        html
      );

      setPreviewVisible(
        true
      );

      setSuccessMessage(
        'Clinical-note preview generated from the current backend documentation.'
      );

      /*
       * Refresh status afterward in case the note changed in another
       * browser/tab before the preview request.
       */
      try {
        const refreshedNote =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );

        setNote(
          refreshedNote
        );

        publishVersion(
          refreshedNote
        );
      } catch {
        /*
         * Preview generation already succeeded.
         * Do not turn a secondary status-refresh failure into a
         * preview failure.
         */
      }
    } catch (previewError) {
      setPreviewHtml('');
      setPreviewVisible(false);

      setError(
        previewError?.message ||
          'Clinical-note preview could not be generated.'
      );
    } finally {
      setLoadingPreview(false);
    }
  }

  /**
   * Open the backend-generated PDF in a new browser tab.
   *
   * A blank tab is opened immediately so normal popup blocking does
   * not interfere while the authenticated PDF request is running.
   */
  async function openPdf() {
    if (
      !clinicalNoteId ||
      openingPdf
    ) {
      return;
    }

    setOpeningPdf(true);
    setError('');
    setSuccessMessage('');

    let pdfWindow =
      null;

    let objectUrl =
      null;

    try {
      /*
       * Open synchronously from the button click.
       */
      pdfWindow =
        window.open(
          '',
          '_blank'
        );

      if (pdfWindow) {
        pdfWindow.document.title =
          'Generating Clinical Note PDF';

        pdfWindow.document.body.innerHTML =
          '<p style="font-family: sans-serif; padding: 24px;">Generating clinical-note PDF...</p>';
      }

      objectUrl =
        await clinicalNoteService
          .createPdfObjectUrl(
            clinicalNoteId
          );

      if (pdfWindow) {
        pdfWindow.location.href =
          objectUrl;
      } else {
        /*
         * Fallback when the browser blocks the initial tab.
         */
        const fallbackWindow =
          window.open(
            objectUrl,
            '_blank',
            'noopener,noreferrer'
          );

        if (!fallbackWindow) {
          throw new Error(
            'The PDF was generated, but the browser blocked the new tab. Allow pop-ups for this site and try again.'
          );
        }
      }

      setSuccessMessage(
        'Clinical-note PDF generated successfully.'
      );

      /*
       * Keep the object URL alive long enough for the new browser tab
       * to finish loading the PDF, then release the frontend blob URL.
       */
      window.setTimeout(
        () => {
          clinicalNoteService
            .revokeObjectUrl(
              objectUrl
            );
        },
        60_000
      );

      objectUrl =
        null;
    } catch (pdfError) {
      if (
        pdfWindow &&
        !pdfWindow.closed
      ) {
        pdfWindow.close();
      }

      if (objectUrl) {
        clinicalNoteService
          .revokeObjectUrl(
            objectUrl
          );
      }

      setError(
        pdfError?.message ||
          'Clinical-note PDF could not be opened.'
      );
    } finally {
      setOpeningPdf(false);
    }
  }

  /**
   * Download the backend-generated PDF.
   */
  async function downloadPdf() {
    if (
      !clinicalNoteId ||
      downloadingPdf
    ) {
      return;
    }

    setDownloadingPdf(true);
    setError('');
    setSuccessMessage('');

    try {
      await clinicalNoteService
        .downloadPdf(
          clinicalNoteId
        );

      setSuccessMessage(
        'Clinical-note PDF download started.'
      );
    } catch (downloadError) {
      setError(
        downloadError?.message ||
          'Clinical-note PDF could not be downloaded.'
      );
    } finally {
      setDownloadingPdf(false);
    }
  }

  /**
   * Close the current HTML preview.
   */
  function closePreview() {
    setPreviewVisible(
      false
    );
  }

  /**
   * Remove the current rendered preview completely.
   */
  function clearPreview() {
    setPreviewVisible(
      false
    );

    setPreviewHtml(
      ''
    );

    setSuccessMessage(
      ''
    );
  }

  if (!enabled) {
    return null;
  }

  const noteStatus =
    note?.status || '';

  const isSigned =
    noteStatus === 'SIGNED';

  const isReadyForReview =
    noteStatus ===
    'READY_FOR_REVIEW';

  const documentBusy =
    loadingPreview ||
    openingPdf ||
    downloadingPdf;

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Final Clinical Document
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Preview and PDF rendering
            are generated by the
            Spring Boot clinical-note
            renderer.
          </div>
        </div>

        <button
          type="button"
          disabled={
            loadingStatus ||
            documentBusy
          }
          onClick={
            loadStatus
          }
        >
          {loadingStatus
            ? 'Refreshing…'
            : 'Refresh Status'}
        </button>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        {/* ===================================================
            DOCUMENT STATUS
            =================================================== */}
        {note && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 9,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                border:
                  isSigned
                    ? '1px solid #a8d5b0'
                    : '1px solid #ddd',
                borderRadius: 8,
                background:
                  isSigned
                    ? '#f4fbf5'
                    : '#fff',
                padding: 10,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: '#777',
                  fontWeight: 700,
                  textTransform:
                    'uppercase',
                }}
              >
                Workflow Status
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontWeight: 700,
                }}
              >
                {formatEnumLabel(
                  noteStatus
                ) || 'Unknown'}
              </div>
            </div>

            <div
              style={{
                border:
                  '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: '#777',
                  fontWeight: 700,
                  textTransform:
                    'uppercase',
                }}
              >
                Note Date
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontWeight: 700,
                }}
              >
                {note.noteDate ||
                  '—'}
              </div>
            </div>

            <div
              style={{
                border:
                  note.locked
                    ? '1px solid #a8d5b0'
                    : '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: '#777',
                  fontWeight: 700,
                  textTransform:
                    'uppercase',
                }}
              >
                Editing
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontWeight: 700,
                }}
              >
                {note.locked
                  ? 'Locked'
                  : 'Editable'}
              </div>
            </div>
          </div>
        )}

        {/* ===================================================
            INFORMATION
            =================================================== */}
        <div
          style={{
            marginBottom: 14,
            border:
              '1px solid #d9e2ec',
            borderRadius: 8,
            background:
              '#f7fafc',
            padding: 11,
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          The browser does not rebuild
          the insurer-facing clinical
          note.

          {' '}

          Preview and PDF requests use
          the same backend-controlled
          clinical documentation,
          templates, payer rules, and
          signature information.
        </div>

        {isReadyForReview && (
          <div
            style={{
              marginBottom: 14,
              border:
                '1px solid #d6a100',
              borderRadius: 8,
              background:
                '#fff8df',
              padding: 10,
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            This note is currently Ready
            For Review but is not yet
            permanently signed.

            {' '}

            The backend will determine
            whether each preview or PDF
            operation is permitted for
            the current note state.
          </div>
        )}

        {isSigned && (
          <div
            style={{
              marginBottom: 14,
              border:
                '1px solid #a8d5b0',
              borderRadius: 8,
              background:
                '#f4fbf5',
              padding: 10,
              fontSize: 12,
              lineHeight: 1.45,
              color: '#276738',
            }}
          >
            This note is permanently
            signed and locked. The final
            rendered document uses the
            frozen clinical signature
            snapshot.
          </div>
        )}

        {/* ===================================================
            ERROR
            =================================================== */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 14,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Document operation could
              not be completed.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {error}
            </div>
          </div>
        )}

        {/* ===================================================
            SUCCESS
            =================================================== */}
        {successMessage && (
          <div
            role="status"
            style={{
              marginBottom: 14,
              border:
                '1px solid #a8d5b0',
              borderRadius: 8,
              background:
                '#f4fbf5',
              padding: 10,
              color: '#276738',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {successMessage}
          </div>
        )}

        {/* ===================================================
            ACTIONS
            =================================================== */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            disabled={
              documentBusy
            }
            onClick={
              loadPreview
            }
          >
            {loadingPreview
              ? 'Generating Preview…'
              : previewHtml
                ? 'Refresh Preview'
                : 'Generate Preview'}
          </button>

          <button
            type="button"
            disabled={
              documentBusy
            }
            onClick={
              openPdf
            }
          >
            {openingPdf
              ? 'Generating PDF…'
              : 'Open PDF'}
          </button>

          <button
            type="button"
            disabled={
              documentBusy
            }
            onClick={
              downloadPdf
            }
          >
            {downloadingPdf
              ? 'Preparing Download…'
              : 'Download PDF'}
          </button>

          {previewHtml && (
            <button
              type="button"
              disabled={
                documentBusy
              }
              onClick={
                previewVisible
                  ? closePreview
                  : () =>
                      setPreviewVisible(
                        true
                      )
              }
            >
              {previewVisible
                ? 'Hide Preview'
                : 'Show Preview'}
            </button>
          )}

          {previewHtml && (
            <button
              type="button"
              disabled={
                documentBusy
              }
              onClick={
                clearPreview
              }
            >
              Clear Preview
            </button>
          )}
        </div>

        {/* ===================================================
            HTML PREVIEW
            =================================================== */}
        {previewVisible &&
          previewHtml && (
          <section
            style={{
              marginTop: 16,
              border:
                '1px solid #bbb',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                alignItems:
                  'center',
                gap: 10,
                flexWrap:
                  'wrap',
                padding:
                  '9px 11px',
                borderBottom:
                  '1px solid #ddd',
                background:
                  '#f7f8fa',
              }}
            >
              <div>
                <strong>
                  Insurer-Facing Preview
                </strong>

                <div
                  style={{
                    marginTop: 2,
                    fontSize: 10,
                    color: '#777',
                  }}
                >
                  Backend-generated HTML
                  preview
                </div>
              </div>

              <button
                type="button"
                onClick={
                  closePreview
                }
              >
                Close Preview
              </button>
            </div>

            {/*
             * sandbox="" intentionally prevents scripts, forms,
             * pop-ups, and parent-page access inside the rendered
             * clinical document.
             *
             * The backend-generated note should be static HTML/CSS.
             */}
            <iframe
              title="Clinical note final preview"
              srcDoc={
                previewHtml
              }
              sandbox=""
              style={{
                display: 'block',
                width: '100%',
                height: '900px',
                border: 0,
                background:
                  '#fff',
              }}
            />
          </section>
        )}

        {/* ===================================================
            VERSION
            =================================================== */}
        {note?.entityVersion !==
          null &&
          note?.entityVersion !==
            undefined && (
          <div
            style={{
              marginTop: 12,
              textAlign: 'right',
              fontSize: 10,
              color: '#999',
            }}
          >
            Clinical note version{' '}
            {note.entityVersion}
          </div>
        )}
      </div>
    </section>
  );
}